import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import MDEditor from "@uiw/react-md-editor";
import {
  Save, ArrowLeft, Loader2, Plus, X, Globe, Image as ImageIcon, Upload, Tag as TagIcon,
  BadgeCheck, FileEdit, Archive, Eye, EyeOff,
} from "lucide-react";
import API from "../lib/api";
import AdminGuard from "../components/admin/AdminGuard";
import AdminShell from "../components/admin/AdminShell";
import { errorMessage } from "../lib/errors";

const LOCALES = [
  { code: "en", label: "English", native: "English", required: true },
  { code: "fr", label: "French", native: "Fran\u00e7ais" },
  { code: "es", label: "Spanish", native: "Espa\u00f1ol" },
  { code: "it", label: "Italian", native: "Italiano" },
  { code: "sw", label: "Swahili", native: "Kiswahili" },
  { code: "ar", label: "Arabic", native: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629", rtl: true },
  { code: "de", label: "German", native: "Deutsch" },
  { code: "nl", label: "Dutch", native: "Nederlands" },
  { code: "zh", label: "Chinese", native: "\u4e2d\u6587" },
  { code: "ko", label: "Korean", native: "\ud55c\uad6d\uc5b4" },
  { code: "ru", label: "Russian", native: "\u0420\u0443\u0441\u0441\u043a\u0438\u0439" },
];

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

function StatusPill({ status }) {
  const { t } = useTranslation();
  const meta = {
    draft: { tone: "bg-white/10 text-white/80 border-white/15", icon: FileEdit },
    published: { tone: "bg-[#C8A24A]/15 text-[#E5C76B] border-[#C8A24A]/40", icon: BadgeCheck },
    archived: { tone: "bg-rose-500/15 text-rose-300 border-rose-500/30", icon: Archive },
  }[status] || { tone: "bg-white/10 text-white/80 border-white/15", icon: FileEdit };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${meta.tone}`}>
      <Icon className="w-3 h-3" />
      {t(`admin.content.status.${status}`, status)}
    </span>
  );
}

export default function AdminContentEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeLocale, setActiveLocale] = useState("en");
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);

  // The whole content_item state — stays close to the API shape.
  const [item, setItem] = useState({
    content_id: null,
    type: "article",
    slug: "",
    status: "draft",
    title_by_locale: { en: "" },
    body_md_by_locale: { en: "" },
    summary_by_locale: { en: "" },
    tags: [],
    topic: "",
    dosha_tags: [],
    hero_image: "",
    meta: {},
  });
  const [tagInput, setTagInput] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Auto-derive slug from EN title (NEW items only) — handled inside updateLocaleField below.

  // Load existing item
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await API.get(`/admin/content/${id}`);
        if (cancelled) return;
        const it = data.item;
        setItem({
          content_id: it.content_id,
          type: it.type || "article",
          slug: it.slug || "",
          status: it.status || "draft",
          title_by_locale: it.title_by_locale || { en: "" },
          body_md_by_locale: it.body_md_by_locale || { en: "" },
          summary_by_locale: it.summary_by_locale || { en: "" },
          tags: it.tags || [],
          topic: it.topic || "",
          dosha_tags: it.dosha_tags || [],
          hero_image: it.hero_image || "",
          meta: it.meta || {},
        });
      } catch (e) {
        if (!cancelled) {
          toast.error(errorMessage(e, t("admin.content.load_one_failed", "Couldn\u2019t load this article")));
          navigate("/admin/content");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew, navigate, t]);

  // Which locales currently have any content (used to render the tab strip).
  const activeLocales = useMemo(() => {
    const codes = new Set(["en"]);
    ["title_by_locale", "body_md_by_locale", "summary_by_locale"].forEach((f) => {
      Object.entries(item[f] || {}).forEach(([k, v]) => {
        if (v && (v + "").trim()) codes.add(k);
      });
    });
    return LOCALES.filter((l) => codes.has(l.code));
  }, [item]);

  const availableToAdd = LOCALES.filter((l) => !activeLocales.find((al) => al.code === l.code));

  const updateLocaleField = (field, locale, value) => {
    setItem((prev) => {
      const next = { ...prev, [field]: { ...(prev[field] || {}), [locale]: value } };
      // Auto-derive slug when editing EN title on a NEW item, and the user hasn't customised the slug.
      if (isNew && !slugManuallyEdited && field === "title_by_locale" && locale === "en") {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const removeLocale = (locale) => {
    if (locale === "en") return;
    setItem((prev) => {
      const newItem = { ...prev };
      ["title_by_locale", "body_md_by_locale", "summary_by_locale"].forEach((f) => {
        const dict = { ...(prev[f] || {}) };
        delete dict[locale];
        newItem[f] = dict;
      });
      return newItem;
    });
    if (activeLocale === locale) setActiveLocale("en");
  };

  const addTag = () => {
    const t1 = (tagInput || "").trim().toLowerCase();
    if (!t1) return;
    if (item.tags.includes(t1)) { setTagInput(""); return; }
    setItem((prev) => ({ ...prev, tags: [...prev.tags, t1] }));
    setTagInput("");
  };

  const removeTag = (t1) => setItem((prev) => ({ ...prev, tags: prev.tags.filter((x) => x !== t1) }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItem((prev) => ({ ...prev, hero_image: data.url }));
      toast.success(t("admin.content.upload_success", "Image uploaded"));
    } catch (err) {
      toast.error(errorMessage(err, t("admin.content.upload_failed", "Couldn\u2019t upload image")));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const validate = (forPublish = false) => {
    const enTitle = (item.title_by_locale?.en || "").trim();
    if (!enTitle) {
      toast.error(t("admin.content.validation.en_title", "English title is required."));
      setActiveLocale("en");
      return false;
    }
    const slug = (item.slug || "").trim();
    if (!slug) {
      toast.error(t("admin.content.validation.slug", "Slug is required."));
      return false;
    }
    if (!SLUG_RE.test(slug)) {
      toast.error(t("admin.content.validation.slug_format", "Slug must be lowercase letters, digits, and hyphens."));
      return false;
    }
    if (forPublish && !(item.body_md_by_locale?.en || "").trim()) {
      toast.error(t("admin.content.validation.body", "English body is recommended before publishing."));
      // soft warning — don't block
    }
    return true;
  };

  const buildPayload = (nextStatus) => ({
    type: "article",
    slug: (item.slug || "").trim().toLowerCase(),
    title_by_locale: item.title_by_locale,
    body_md_by_locale: item.body_md_by_locale,
    summary_by_locale: item.summary_by_locale,
    tags: item.tags,
    topic: (item.topic || "").trim() || null,
    dosha_tags: item.dosha_tags,
    hero_image: (item.hero_image || "").trim() || null,
    status: nextStatus ?? item.status,
    meta: item.meta,
  });

  const handleSave = async (nextStatus = null) => {
    const isPublishing = nextStatus === "published";
    if (!validate(isPublishing)) return;
    setSaving(true);
    try {
      const payload = buildPayload(nextStatus);
      let data;
      if (isNew) {
        const res = await API.post("/admin/content", payload);
        data = res.data;
      } else {
        const res = await API.put(`/admin/content/${item.content_id}`, payload);
        data = res.data;
      }
      const saved = data.item;
      toast.success(
        nextStatus === "published"
          ? t("admin.content.publish_success", "Published \u2018{{slug}}\u2019", { slug: saved.slug })
          : t("admin.content.save_success", "Saved \u2018{{slug}}\u2019", { slug: saved.slug })
      );
      if (isNew) {
        navigate(`/admin/content/${saved.content_id}/edit`, { replace: true });
      } else {
        setItem({
          content_id: saved.content_id,
          type: saved.type,
          slug: saved.slug,
          status: saved.status,
          title_by_locale: saved.title_by_locale || { en: "" },
          body_md_by_locale: saved.body_md_by_locale || { en: "" },
          summary_by_locale: saved.summary_by_locale || { en: "" },
          tags: saved.tags || [],
          topic: saved.topic || "",
          dosha_tags: saved.dosha_tags || [],
          hero_image: saved.hero_image || "",
          meta: saved.meta || {},
        });
      }
    } catch (err) {
      toast.error(errorMessage(err, t("admin.content.save_failed", "Couldn\u2019t save content")));
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!item.content_id) return;
    if (!window.confirm(t("admin.content.confirm_unpublish", "Unpublish this article? It will move to draft."))) return;
    setSaving(true);
    try {
      const { data } = await API.post(`/admin/content/${item.content_id}/unpublish`);
      setItem((prev) => ({ ...prev, status: data.item?.status || "draft" }));
      toast.success(t("admin.content.unpublish_success", "Moved to draft"));
    } catch (err) {
      toast.error(errorMessage(err, t("admin.content.unpublish_failed", "Couldn\u2019t unpublish")));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminGuard>
        <AdminShell active="content">
          <div className="min-h-[40vh] grid place-items-center">
            <div className="flex items-center gap-2 text-white/65 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{t("admin.loading", "Loading\u2026")}</div>
          </div>
        </AdminShell>
      </AdminGuard>
    );
  }

  const activeLocaleMeta = LOCALES.find((l) => l.code === activeLocale) || LOCALES[0];
  const isRtl = !!activeLocaleMeta.rtl;
  const currentTitle = item.title_by_locale?.[activeLocale] || "";
  const currentSummary = item.summary_by_locale?.[activeLocale] || "";
  const currentBody = item.body_md_by_locale?.[activeLocale] || "";

  return (
    <AdminGuard>
      <AdminShell active="content">
        <div className="space-y-6" data-testid="admin-content-editor">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <Link to="/admin/content" className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1 mb-1" data-testid="back-to-list">
                <ArrowLeft className="w-3 h-3" /> {t("admin.content.back_to_list", "Back to list")}
              </Link>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="editor-title">
                  {isNew ? t("admin.content.new_article", "New article") : (item.title_by_locale?.en || item.slug || t("admin.content.untitled", "Untitled"))}
                </h2>
                <StatusPill status={item.status} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleSave("draft")}
                disabled={saving}
                className="btn-ghost text-xs inline-flex items-center gap-1.5"
                data-testid="save-draft-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {t("admin.content.save_draft", "Save draft")}
              </button>
              {item.status === "published" ? (
                <button
                  type="button"
                  onClick={handleUnpublish}
                  disabled={saving}
                  className="btn-ghost text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1.5"
                  data-testid="unpublish-btn"
                >
                  <EyeOff className="w-3.5 h-3.5" />
                  {t("admin.content.unpublish", "Unpublish")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSave("published")}
                  disabled={saving}
                  className="btn-primary text-xs inline-flex items-center gap-1.5"
                  data-testid="publish-btn"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />}
                  {t("admin.content.publish", "Publish")}
                </button>
              )}
            </div>
          </div>

          {/* Body grid: editor (left) + meta rail (right) */}
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            {/* Left: locale tabs + fields */}
            <div className="space-y-4 min-w-0">
              {/* Locale tabs */}
              <div className="card-surface p-3" data-testid="locale-tabs">
                <div className="flex items-center gap-1 flex-wrap">
                  {activeLocales.map((loc) => (
                    <button
                      key={loc.code}
                      type="button"
                      onClick={() => setActiveLocale(loc.code)}
                      className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 transition-colors ${
                        activeLocale === loc.code
                          ? "bg-[#C8A24A]/15 border-[#C8A24A]/40 text-[#E5C76B]"
                          : "border-white/10 text-white/65 hover:text-white hover:border-white/30"
                      }`}
                      data-testid={`locale-tab-${loc.code}`}
                    >
                      <Globe className="w-3 h-3" />
                      <span className="uppercase font-medium">{loc.code}</span>
                      <span className="opacity-60">{loc.native}</span>
                      {loc.required && <span className="text-[9px] gold-text">{t("admin.content.required", "REQ")}</span>}
                      {!loc.required && activeLocales.length > 1 && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); removeLocale(loc.code); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); removeLocale(loc.code); } }}
                          className="ms-1 opacity-50 hover:opacity-100"
                          aria-label={t("admin.content.remove_locale", "Remove locale")}
                        >
                          <X className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  ))}
                  {availableToAdd.length > 0 && (
                    <div className="relative">
                      <select
                        value=""
                        onChange={(e) => { if (e.target.value) setActiveLocale(e.target.value); }}
                        className="text-xs bg-[#0F1116] border border-white/15 rounded-md px-2 py-1.5 text-white/70 hover:text-white focus:outline-none focus:border-[#C8A24A]/40"
                        data-testid="add-locale"
                      >
                        <option value="">+ {t("admin.content.add_locale", "Add language\u2026")}</option>
                        {availableToAdd.map((loc) => (
                          <option key={loc.code} value={loc.code}>{loc.code.toUpperCase()} · {loc.native}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {activeLocale !== "en" && (
                  <p className="text-[11px] text-white/50 mt-2">
                    {t("admin.content.locale_hint", "This locale is optional. If left empty, viewers will see the English content as fallback.")}
                  </p>
                )}
              </div>

              {/* Title */}
              <div className="card-surface p-4 space-y-3" data-testid="locale-fields">
                <div>
                  <label className="text-xs text-white/55 mb-1 block" htmlFor="f-title">
                    {t("admin.content.field_title", "Title")}
                    {activeLocale === "en" && <span className="gold-text ms-1">*</span>}
                  </label>
                  <input
                    id="f-title"
                    type="text"
                    value={currentTitle}
                    onChange={(e) => updateLocaleField("title_by_locale", activeLocale, e.target.value)}
                    placeholder={activeLocale === "en" ? t("admin.content.title_placeholder_en", "Your headline\u2026") : t("admin.content.title_placeholder_other", "Optional \u2014 leave empty to use English")}
                    className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-base focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                    dir={isRtl ? "rtl" : "ltr"}
                    data-testid={`field-title-${activeLocale}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-white/55 mb-1 block" htmlFor="f-summary">{t("admin.content.field_summary", "Summary")}</label>
                  <textarea
                    id="f-summary"
                    rows={2}
                    value={currentSummary}
                    onChange={(e) => updateLocaleField("summary_by_locale", activeLocale, e.target.value)}
                    placeholder={t("admin.content.summary_placeholder", "One or two sentences describing this article\u2026")}
                    className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C8A24A]/40 transition-colors resize-y"
                    dir={isRtl ? "rtl" : "ltr"}
                    data-testid={`field-summary-${activeLocale}`}
                  />
                </div>
              </div>

              {/* Body (Markdown editor) */}
              <div className="card-surface p-4" data-testid="body-editor">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-white/55">{t("admin.content.field_body", "Body (Markdown)")}</label>
                  <button
                    type="button"
                    onClick={() => setShowPreview((s) => !s)}
                    className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1"
                    data-testid="toggle-preview"
                  >
                    {showPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {showPreview ? t("admin.content.hide_preview", "Hide preview") : t("admin.content.show_preview", "Show preview")}
                  </button>
                </div>
                <div data-color-mode="dark" className={`md-editor-wrap ${isRtl ? "rtl" : ""}`}>
                  <MDEditor
                    value={currentBody}
                    onChange={(v) => updateLocaleField("body_md_by_locale", activeLocale, v || "")}
                    height={500}
                    preview={showPreview ? "live" : "edit"}
                    visibleDragbar={false}
                  />
                </div>
              </div>
            </div>

            {/* Right rail: meta */}
            <aside className="space-y-4">
              {/* Slug */}
              <div className="card-surface p-4">
                <label className="text-xs text-white/55 mb-1 block" htmlFor="f-slug">{t("admin.content.field_slug", "Slug")}<span className="gold-text ms-1">*</span></label>
                <input
                  id="f-slug"
                  type="text"
                  value={item.slug}
                  onChange={(e) => { setSlugManuallyEdited(true); setItem((p) => ({ ...p, slug: e.target.value })); }}
                  placeholder="my-article-slug"
                  className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                  data-testid="field-slug"
                />
                <p className="text-[11px] text-white/45 mt-1">{t("admin.content.slug_hint", "Lowercase letters, digits, and hyphens. Used in the public URL.")}</p>
              </div>

              {/* Hero image */}
              <div className="card-surface p-4">
                <label className="text-xs text-white/55 mb-2 block">{t("admin.content.field_hero", "Hero image")}</label>
                {item.hero_image ? (
                  <div className="rounded-lg overflow-hidden border border-white/10 mb-2">
                    <img src={item.hero_image} alt="Hero" className="w-full h-32 object-cover" data-testid="hero-preview" />
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-white/15 h-32 grid place-items-center mb-2 text-white/40">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="btn-ghost text-xs cursor-pointer inline-flex items-center gap-1.5" data-testid="upload-label">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {t("admin.content.upload", "Upload")}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} data-testid="upload-input" />
                  </label>
                  {item.hero_image && (
                    <button type="button" onClick={() => setItem((p) => ({ ...p, hero_image: "" }))} className="btn-ghost text-xs text-rose-300" data-testid="clear-hero">{t("admin.content.clear", "Clear")}</button>
                  )}
                </div>
                <input
                  type="text"
                  value={item.hero_image}
                  onChange={(e) => setItem((p) => ({ ...p, hero_image: e.target.value }))}
                  placeholder={t("admin.content.hero_url_placeholder", "Or paste an image URL\u2026")}
                  className="w-full mt-2 bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                  data-testid="hero-url"
                />
              </div>

              {/* Topic */}
              <div className="card-surface p-4">
                <label className="text-xs text-white/55 mb-1 block" htmlFor="f-topic">{t("admin.content.field_topic", "Topic")}</label>
                <input
                  id="f-topic"
                  type="text"
                  value={item.topic}
                  onChange={(e) => setItem((p) => ({ ...p, topic: e.target.value }))}
                  placeholder={"mens-wellness, sleep, nutrition\u2026"}
                  className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                  data-testid="field-topic"
                />
              </div>

              {/* Tags */}
              <div className="card-surface p-4">
                <label className="text-xs text-white/55 mb-2 block">{t("admin.content.field_tags", "Tags")}</label>
                <div className="flex flex-wrap gap-1 mb-2" data-testid="tags-list">
                  {item.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 inline-flex items-center gap-1">
                      <TagIcon className="w-2.5 h-2.5 gold-text" />
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="opacity-50 hover:opacity-100" aria-label="Remove tag">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    placeholder={t("admin.content.tag_placeholder", "Add a tag\u2026")}
                    className="flex-1 bg-[#0F1116] border border-white/10 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                    data-testid="tag-input"
                  />
                  <button type="button" onClick={addTag} className="btn-ghost p-1.5" aria-label={t("admin.content.add_tag", "Add tag")} data-testid="add-tag-btn">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Public preview link */}
              {!isNew && item.slug && (
                <div className="card-surface p-4">
                  <div className="text-xs text-white/55 mb-1">{t("admin.content.public_url", "Public URL")}</div>
                  <a
                    href={`/article/${item.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs gold-text hover:underline break-all"
                    data-testid="public-url-link"
                  >
                    /article/{item.slug}
                  </a>
                </div>
              )}
            </aside>
          </div>
        </div>
      </AdminShell>
    </AdminGuard>
  );
}
